/**
 * Weergave-helpers voor de datakwaliteitsscore (0–100, hele punten).
 *
 * De kleurbanden zijn een voorlopige UI-keuze; de formele drempel voor
 * benchmarkdeelname komt pas uit de kalibratie (schaduwdraai) en staat in
 * functions/dataQuality/parameters.js. De band van 70 volgt de werkhypothese
 * uit de appendix. Zie docs/features/datakwaliteitsscore.md.
 *
 * @param {number|null|undefined} score
 * @returns {'laag'|'midden'|'hoog'|null} Band voor kleurklassen, null zonder score
 */
export const dataQualityBand = (score) => {
    if (typeof score !== 'number' || Number.isNaN(score)) return null
    if (score < 50) return 'laag'
    if (score < 70) return 'midden'
    return 'hoog'
}

/**
 * Labels per dimensie voor kaart- en detailweergave.
 * @type {Record<string, string>}
 */
export const DIMENSION_LABELS = {
    dekking: 'Dekking',
    volledigheid: 'Volledigheid',
    integriteit: 'Dataintegriteit',
    actualiteit: 'Actualiteit en continuïteit',
}

/**
 * Labels per indicator (appendix-codes) voor de detailweergave.
 * @type {Record<string, string>}
 */
export const INDICATOR_LABELS = {
    D1: 'Dekkingspercentage',
    D2: 'Complete meetmomenten',
    D3: 'Spreiding over de tijd',
    D4: 'Spreiding over trajecten',
    V1: 'Volledig ingevulde vragenlijsten',
    V2: 'Beantwoorde vragen',
    V3: 'Gekoppelde voor- én nameting',
    V4: 'Achtergrond- en trajectgegevens',
    A1: 'Tijd sinds laatste registratie',
    A2: 'Aandeel actuele data',
    A3: 'Continuïteit',
}

/**
 * Korte uitleg per dimensie voor de detail-modal.
 * @type {Record<string, string>}
 */
export const DIMENSION_EXPLANATIONS = {
    dekking: 'Geeft de data voldoende zicht op de deelnemers? Gemeten wordt hoeveel van de relevante deelnemers, meetmomenten, maanden en trajecten daadwerkelijk in de data terugkomen.',
    volledigheid: 'Wat ontbreekt er in de verzamelde data? Gemeten wordt hoe compleet de meetstandaard-vragen zijn ingevuld en of voor- en nametingen aan elkaar gekoppeld zijn. Eigen (vrije) vragen zijn optioneel en tellen niet mee.',
    integriteit: 'Wat klopt er aantoonbaar niet? Een record telt als fout bij een antwoord buiten het toegestane bereik, een nameting die vóór de voormeting is geregistreerd, een dubbele invulling van dezelfde vragenlijst op hetzelfde meetmoment, of een verwijzing naar een verwijderde vraag. Een record met meerdere fouten telt éénmaal.',
    actualiteit: 'Hoe recent en regelmatig wordt er geregistreerd? Gemeten worden de tijd sinds de laatste registratie, het aandeel recente data en de langste onderbreking.',
}

/**
 * Korte uitleg per indicator voor de detail-modal.
 * @type {Record<string, string>}
 */
export const INDICATOR_EXPLANATIONS = {
    D1: 'Unieke gemeten deelnemers gedeeld door het aantal relevante deelnemers. Zonder deelnemersregister telt deze indicator als 0.',
    D2: 'Deelnemers met alle voor hun traject vereiste meetmomenten, gedeeld door het aantal gemeten deelnemers.',
    D3: 'Actieve maanden met minimaal één registratie, gedeeld door het aantal actieve maanden.',
    D4: 'Actieve trajecten of doelgroepen met minimaal één meting, gedeeld door het totaal.',
    V1: 'Inzendingen waarin alle meetstandaard-vragen zijn beantwoord (streng: één gemiste verplichte vraag maakt de lijst onvolledig; eigen vragen tellen niet mee).',
    V2: 'Het aandeel beantwoorde meetstandaard-vragen over alle inzendingen (mild: telt de omvang van het gat).',
    V3: 'Deelnemers met een gekoppelde voor- én nameting, gedeeld door de deelnemers van wie een nameting wordt verwacht (afgerond traject).',
    V4: 'Volledigheid van achtergrond- en trajectgegevens — wacht op gestandaardiseerde kenmerken en telt nog niet mee.',
    A1: '100 punten tot 3 maanden na de laatste registratie, daarna aflopend naar 0 bij 12 maanden.',
    A2: 'Aandeel records uit de laatste 12 maanden; vanaf 40% is de score maximaal.',
    A3: 'De langste registratie-onderbreking: 100 punten bij hoogstens 2 maanden, 0 vanaf 6 maanden.',
}

/**
 * Telling-regel per indicator, opgebouwd uit de opgeslagen breakdown-counts.
 * Retourneert null wanneer de tellingen (nog) niet in het score-doc staan.
 *
 * @param {string} code - Indicatorcode (D1..D4, V1..V4, A1..A3)
 * @param {object|undefined} counts - `counts` van de dimensie uit de breakdown
 * @returns {string|null}
 */
export const indicatorCountLine = (code, counts) => {
    if (!counts) return null
    switch (code) {
        case 'D1': return `${counts.measuredParticipants} van ${counts.relevantParticipants} relevante deelnemers gemeten`
        case 'D2': return `${counts.participantsWithAllMoments} van ${counts.measuredParticipants} gemeten deelnemers met alle vereiste meetmomenten`
        case 'D3': return `${counts.monthsWithRegistration} van ${counts.activeMonths} actieve maanden met een registratie`
        case 'D4': return `${counts.groupsMeasured} van ${counts.groupsTotal} trajecten of doelgroepen gemeten`
        case 'V1': return `${counts.completeSubmissions} van ${counts.submissionCount} inzendingen volledig ingevuld`
        case 'V2': return `${counts.totalMissing} van ${counts.totalExpected} verwachte antwoorden ontbreken`
        case 'V3': return `${counts.participantsWithPair} van ${counts.participantsExpectingPost} verwachte nametingen gekoppeld`
        case 'A1': return counts.lastRegistrationMs
            ? `Laatste registratie op ${new Date(counts.lastRegistrationMs).toLocaleDateString('nl-NL')}`
            : 'Nog geen registraties'
        case 'A2': return `${counts.recentCount} van ${counts.totalCount} records uit de laatste 12 maanden`
        case 'A3': return counts.longestGapMonths === null
            ? 'Geen actieve maanden — onderbreking niet berekenbaar'
            : `Langste onderbreking: ${Math.round(counts.longestGapMonths)} ${Math.round(counts.longestGapMonths) === 1 ? 'maand' : 'maanden'}`
        default: return null
    }
}

/**
 * Nederlandse labels per integriteits-fouttype (recordValidity.ERROR_TYPES).
 * @type {Record<string, string>}
 */
export const ERROR_TYPE_LABELS = {
    out_of_range: 'Antwoord buiten het toegestane bereik',
    orphan_reference: 'Verwijzing naar een vraag of rij die niet bestaat',
    duplicate_submission: 'Dubbele invulling van dezelfde vragenlijst',
    pre_post_order: 'Nameting geregistreerd vóór de voormeting',
}

/**
 * Toelichting bij antwoorden op AFGEVOERDE vragen (integriteit).
 *
 * Haalt een organisatie een effect uit haar TOC, dan blijven de al gegeven
 * antwoorden staan. Die zijn geldig en tellen sinds Meetstandaard 2026.1 niet
 * meer als meetfout. Zonder deze regel zakt het aantal foute records zonder
 * uitleg en lijkt er data verdwenen.
 *
 * Geeft '' zolang het aantal onbekend is — 'nog niet geladen' mag nooit als
 * 'nul' lezen.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {number|null|undefined} retiredRecords - Aantal antwoorden op afgevoerde vragen
 * @returns {string} Lege string wanneer er niets te melden is
 */
export const retiredAnswersNote = (retiredRecords) => {
    if (typeof retiredRecords !== 'number' || retiredRecords <= 0) return ''
    if (retiredRecords === 1) {
        return '1 antwoord hoort bij een vraag die je hebt afgevoerd. '
            + 'Die telt niet mee — er is niets op te schonen.'
    }
    return `${retiredRecords} antwoorden horen bij vragen die je hebt afgevoerd. `
        + 'Die tellen niet mee — er is niets op te schonen.'
}

const MONTH_NAMES_NL = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

/**
 * 'YYYY-MM' → 'april 2026' voor de dimensiepagina's.
 *
 * @param {string} monthKey
 * @returns {string}
 */
export const formatMonthLabel = (monthKey) => {
    const [year, month] = String(monthKey).split('-')
    const name = MONTH_NAMES_NL[Number(month) - 1]
    return name ? `${name} ${year}` : monthKey
}

/**
 * @typedef {object} ScoreGrowth
 * @property {number} delta - Huidige score min die van de momentopname
 * @property {string} month - Maand van de momentopname, 'YYYY-MM'
 * @property {number} previousScore - Score in de momentopname
 */

/**
 * Groei van de score als zin voor de kaart en de detailpagina (besluit
 * 2026-10-13). De momentopname is de stand aan het EINDE van die maand, vandaar
 * 'sinds eind juli'. Het jaar staat er alleen bij als het een ander jaar is.
 *
 * @param {ScoreGrowth|null|undefined} growth - Uit de getDataQualityScore-callable
 * @param {Date} [now]
 * @returns {{text: string, direction: 'up'|'down'|'flat'}|null}
 */
export const formatScoreGrowth = (growth, now = new Date()) => {
    if (!growth || typeof growth.delta !== 'number' || !growth.month) return null
    const [year, month] = growth.month.split('-')
    const name = MONTH_NAMES_NL[Number(month) - 1] || growth.month
    const when = Number(year) === now.getUTCFullYear() ? `eind ${name}` : `eind ${name} ${year}`
    if (growth.delta > 0) return { text: `+${growth.delta} sinds ${when}`, direction: 'up' }
    if (growth.delta < 0) return { text: `−${Math.abs(growth.delta)} sinds ${when}`, direction: 'down' }
    return { text: `Gelijk aan ${when}`, direction: 'flat' }
}

export default dataQualityBand
